import React, { useState } from 'react';
import { 
  Search, Filter, Download, ChevronLeft, ChevronRight, 
  Star, Trash2, Flag, Eye, User, Package, Calendar,
  CheckCircle, XCircle, AlertCircle, MessageSquare,
  MoreVertical, ThumbsUp, ThumbsDown, Clock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

const Reviews = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 5;

  const fetchReviews = async () => {
    try {
      let token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        token = userInfo.token;
      }
      const res = await axios.get('/api/admin/reviews', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const formattedReviews = res.data.data.map(rev => ({
        id: rev._id,
        product: { 
            id: rev.product?._id, 
            name: rev.product?.name || 'Unknown', 
            image: rev.product?.image || '🪴', 
            seller: rev.product?.seller?.username || 'Unknown Seller' 
        },
        customer: { 
            name: rev.user?.username || 'Anonymous', 
            avatar: (rev.user?.username || 'A').charAt(0).toUpperCase(), 
            email: rev.user?.email || 'N/A' 
        },
        rating: rev.rating,
        comment: rev.message,
        date: new Date(rev.createdAt).toLocaleDateString(),
        time: new Date(rev.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
        status: rev.flagged ? 'flagged' : 'approved',
        helpful: rev.helpful || 0,
        flagged: rev.flagged || false
      }));
      setReviews(formattedReviews);
    } catch (error) {
      console.error('Failed to fetch admin reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchReviews();
  }, []);
  // Empty for now

  const stats = {
    total: reviews.length,
    averageRating: (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1),
    approved: reviews.filter(r => r.status === 'approved').length,
    flagged: reviews.filter(r => r.flagged).length,
    fiveStar: reviews.filter(r => r.rating === 5).length,
    fourStar: reviews.filter(r => r.rating === 4).length,
    threeStar: reviews.filter(r => r.rating === 3).length,
    twoStar: reviews.filter(r => r.rating === 2).length,
    oneStar: reviews.filter(r => r.rating === 1).length
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'approved': return { label: 'Approved', icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'flagged': return { label: 'Flagged', icon: Flag, color: '#ef4444', bg: '#fee2e2' };
      default: return { label: 'Pending', icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
    }
  };

  const filteredReviews = reviews.filter(review => {
    const matchesSearch = review.product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          review.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          review.comment.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRating = filterRating === 'all' || review.rating.toString() === filterRating;
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'flagged' && review.flagged) ||
      (filterStatus === 'approved' && !review.flagged);
    return matchesSearch && matchesRating && matchesStatus;
  });

  const totalPages = Math.ceil(filteredReviews.length / itemsPerPage);
  const paginatedReviews = filteredReviews.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const handleDelete = async (reviewId) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        let token = localStorage.getItem('token') || sessionStorage.getItem('token');
        await axios.delete(`/api/admin/reviews/${reviewId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Review deleted successfully`);
        fetchReviews();
      } catch (err) {
        toast.error('Failed to delete review');
      }
    }
  };

  const handleFlag = async (reviewId) => {
    // Optional: implement flag logic on backend if needed, or just toast for now.
    toast.info(`Review flagged`);
  };

  const handleApprove = async (reviewId) => {
    toast.info(`Review approved`);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        size={14} 
        fill={i < rating ? '#f59e0b' : 'transparent'} 
        color={i < rating ? '#f59e0b' : '#cbd5e1'}
        className="star-icon"
      />
    ));
  };

  return (
    <div className="admin-reviews-modern">
      {/* Header */}
      <div className="reviews-header">
        <div>
          <h1 className="page-title">Customer Reviews</h1>
          <p className="page-subtitle">Manage and moderate customer feedback</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export Reviews
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Reviews" value={stats.total} icon={MessageSquare} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Average Rating" value={`${stats.averageRating} / 5.0`} icon={Star} color="#f59e0b" bgColor="#fed7aa" />
        <StatCard title="Approved" value={stats.approved} icon={CheckCircle} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Flagged" value={stats.flagged} icon={Flag} color="#ef4444" bgColor="#fee2e2" />
      </div>

      {/* Rating Distribution */}
      <div className="rating-distribution">
        <div className="distribution-header">
          <h4>Rating Breakdown</h4>
        </div>
        <div className="distribution-bars">
          {[
            { stars: 5, count: stats.fiveStar, percentage: (stats.fiveStar / stats.total) * 100 },
            { stars: 4, count: stats.fourStar, percentage: (stats.fourStar / stats.total) * 100 },
            { stars: 3, count: stats.threeStar, percentage: (stats.threeStar / stats.total) * 100 },
            { stars: 2, count: stats.twoStar, percentage: (stats.twoStar / stats.total) * 100 },
            { stars: 1, count: stats.oneStar, percentage: (stats.oneStar / stats.total) * 100 }
          ].map((item) => (
            <div key={item.stars} className="distribution-item">
              <div className="star-label">
                <span>{item.stars}</span>
                <Star size={12} fill="#f59e0b" color="#f59e0b" />
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${item.percentage}%` }}></div>
              </div>
              <div className="count-label">{item.count}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by product, customer or comment..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterRating} onChange={(e) => setFilterRating(e.target.value)}>
            <option value="all">All Ratings</option>
            <option value="5">5 Stars ⭐⭐⭐⭐⭐</option>
            <option value="4">4 Stars ⭐⭐⭐⭐</option>
            <option value="3">3 Stars ⭐⭐⭐</option>
            <option value="2">2 Stars ⭐⭐</option>
            <option value="1">1 Star ⭐</option>
          </select>
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="approved">Approved</option>
            <option value="flagged">Flagged</option>
          </select>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <span>{filteredReviews.length} Reviews Found</span>
      </div>

      {/* Reviews Table */}
      <div className="table-container">
        <div className="table-responsive">
          <table className="reviews-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Customer</th>
                <th>Rating</th>
                <th>Review</th>
                <th>Date</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedReviews.map((review) => {
                const statusConfig = getStatusConfig(review.flagged ? 'flagged' : 'approved');
                const StatusIcon = statusConfig.icon;
                
                return (
                  <tr key={review.id}>
                    <td className="product-cell">
                      <div className="product-info">
                        <div className="product-icon">{review.product.image}</div>
                        <div>
                          <div className="product-name">{review.product.name}</div>
                          <div className="product-seller">{review.product.seller}</div>
                        </div>
                      </div>
                    </td>
                    <td className="customer-cell">
                      <div className="customer-avatar">
                        <span>{review.customer.avatar}</span>
                      </div>
                      <div>
                        <div className="customer-name">{review.customer.name}</div>
                        <div className="customer-email">{review.customer.email}</div>
                      </div>
                    </td>
                    <td className="rating-cell">
                      <div className="rating-stars">{renderStars(review.rating)}</div>
                      <span className="rating-value">{review.rating}.0</span>
                    </td>
                    <td className="review-cell">
                      <div className="review-comment">{review.comment}</div>
                      <div className="review-helpful">
                        <ThumbsUp size={12} />
                        <span>{review.helpful} found this helpful</span>
                      </div>
                    </td>
                    <td className="date-cell">
                      <div className="date-info">
                        <Calendar size={12} />
                        <span>{review.date}</span>
                        <span className="time">{review.time}</span>
                      </div>
                    </td>
                    <td>
                      <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="action-buttons">
                        {review.flagged && (
                          <button className="action-btn approve" onClick={() => handleApprove(review.id)} title="Approve">
                            <CheckCircle size={16} />
                          </button>
                        )}
                        {!review.flagged && (
                          <button className="action-btn flag" onClick={() => handleFlag(review.id)} title="Flag as Inappropriate">
                            <Flag size={16} />
                          </button>
                        )}
                        <button className="action-btn delete" onClick={() => handleDelete(review.id)} title="Delete Review">
                          <Trash2 size={16} />
                        </button>
                        <button className="action-btn view" title="View Details">
                          <Eye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            <div className="pagination-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredReviews.length)} of {filteredReviews.length} reviews
            </div>
            <div className="pagination-buttons">
              <button 
                className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      
    </div>
  );
};

export default Reviews;